import { useState } from "react";
import { organizationApiService } from "../../../services/apiV2Service";
import billingService from "../../../services/billingService";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";

export const useHostOrganizationEdit = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    address: "",
    contactEmail: "",
    contactMsisdn: "",
    status: "",
    spaceType: "LOW",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);

  // Revenue share state
  const [commissionPercentage, setCommissionPercentage] = useState(0);
  const [isCustomRevenueShare, setIsCustomRevenueShare] = useState(false);
  const [defaultCommission, setDefaultCommission] = useState(0);

  const handleEditShow = async organization => {
    setSelectedOrganization(organization);
    setEditError(null);
    setShowEditModal(true);
    setFetchingData(true);

    try {
      // Fetch the latest organization data
      const response = await organizationApiService.getOrganizationById(organization.id);
      const orgData = response.data?.data || organization;

      // Populate form
      setEditFormData({
        name: orgData.name || "",
        address: orgData.address || "",
        contactEmail: orgData.contactEmail || "",
        contactMsisdn: orgData.contactMsisdn || "",
        status: orgData.status || "",
        spaceType: orgData.spaceType || "LOW",
      });

      // Load revenue share data
      try {
        const orgResponse = await billingService.getOrganizationRevenueShare(orgData.id);
        if (orgResponse.status === 200) {
          const data = orgResponse.data?.data || orgResponse.data;
          // Convert decimal to percentage for display (multiply by 100)
          const commissionDecimal = data.commissionPercentage || 0;
          setCommissionPercentage(commissionDecimal * 100);
          setIsCustomRevenueShare(data.custom || false);
        }
      } catch (err) {
        logger.error("Error loading revenue share:", err);
      }

      // Load default revenue share
      try {
        const defaultResponse = await billingService.getDefaultRevenueShare();
        if (defaultResponse.status === 200) {
          const data = defaultResponse.data?.data || defaultResponse.data;
          // Convert decimal to percentage for display (multiply by 100)
          const defaultDecimal = data.commissionPercentage || 0;
          setDefaultCommission(defaultDecimal * 100);
        }
      } catch (err) {
        logger.error("Error loading default revenue share:", err);
      }
    } catch (error) {
      logger.error("Error fetching organization data:", error);
      setEditError("Failed to load organization data. Please try again.");
      // Fallback to using passed organization data
      setEditFormData({
        name: organization.name || "",
        address: organization.address || "",
        contactEmail: organization.contactEmail || "",
        contactMsisdn: organization.contactMsisdn || "",
        status: organization.status || "",
        spaceType: organization.spaceType || "LOW",
      });
    } finally {
      setFetchingData(false);
    }
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setSelectedOrganization(null);
    setEditFormData({
      name: "",
      address: "",
      contactEmail: "",
      contactMsisdn: "",
      status: "",
      spaceType: "LOW",
    });
    setCommissionPercentage(0);
    setIsCustomRevenueShare(false);
    setEditError(null);
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!selectedOrganization) return;

    if (!editFormData.name || !editFormData.contactEmail) {
      setEditError("Please fill in all required fields (Name, Contact Email)");
      return;
    }

    setEditLoading(true);
    setEditError(null);

    try {
      // Update organization details
      const payload = {
        name: editFormData.name,
        address: editFormData.address || "",
        contactEmail: editFormData.contactEmail,
        contactMsisdn: editFormData.contactMsisdn || "",
        status: editFormData.status,
        spaceType: editFormData.spaceType || "LOW",
      };

      const response = await organizationApiService.updateOrganization(selectedOrganization.id, payload);

      if (response.status === 200) {
        showApiMessage(response, "update organization");

        // Update revenue share if commission percentage is provided
        if (commissionPercentage !== undefined && commissionPercentage !== null) {
          try {
            // Convert percentage back to decimal for API (divide by 100)
            const commissionDecimal = parseFloat(commissionPercentage) / 100;
            const revenueResponse = await billingService.setOrganizationRevenueShare(selectedOrganization.id, {
              commissionPercentage: commissionDecimal,
            });

            if (revenueResponse.status === 200) {
              showApiMessage(revenueResponse, "update revenue share");
            }
          } catch (revenueErr) {
            logger.error("Error updating revenue share:", revenueErr);
            showApiMessage(revenueErr, "update revenue share");
          }
        }

        handleEditClose();
        return { success: true };
      }
    } catch (error) {
      logger.error("Error updating organization:", error);
      setEditError("Failed to update organization. Please try again.");
      showApiMessage(error, "update organization");
      return { success: false, error };
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteRevenueShare = async () => {
    if (!selectedOrganization) return;

    try {
      // First, fetch the current default revenue share value
      let currentDefaultCommission = defaultCommission;
      try {
        const defaultResponse = await billingService.getDefaultRevenueShare();
        if (defaultResponse.status === 200) {
          const data = defaultResponse.data?.data || defaultResponse.data;
          // Convert decimal to percentage for display (multiply by 100)
          const defaultDecimal = data.commissionPercentage || 0;
          currentDefaultCommission = defaultDecimal * 100;
          // Update the state with the fresh default value
          setDefaultCommission(currentDefaultCommission);
        }
      } catch (defaultErr) {
        logger.error("Error fetching default revenue share:", defaultErr);
        // Continue with existing defaultCommission if fetch fails
      }

      // Delete the custom revenue share
      const response = await billingService.deleteOrganizationRevenueShare(selectedOrganization.id);

      if (response.status === 200 || response.status === 204) {
        showApiMessage(response, "reset revenue share to default");
        // Set commission percentage to the fresh default value
        setCommissionPercentage(currentDefaultCommission);
        setIsCustomRevenueShare(false);
      }
    } catch (err) {
      logger.error("Error deleting revenue share:", err);
      showApiMessage(err, "reset revenue share to default");
    }
  };

  return {
    showEditModal,
    setShowEditModal,
    selectedOrganization,
    editFormData,
    editLoading,
    editError,
    fetchingData,
    commissionPercentage,
    setCommissionPercentage,
    isCustomRevenueShare,
    defaultCommission,
    handleEditShow,
    handleEditClose,
    handleInputChange,
    handleSubmit,
    handleDeleteRevenueShare,
  };
};
